import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';
import * as yup from "yup";
import { useRef, useState } from 'react';
import { isAfter, isValid, parse } from 'date-fns';

const schema = yup.object().shape({
    name: yup
        .string()
        .required('name is required')
        .trim()
        .max(255, 'name too long')
        .matches(/^[\p{L}' -]+$/u, 'invalid name format'),
    number: yup
        .string()
        .required('card number is required')
        .trim()
        .min(14, 'card number too short')
        .max(19, 'card number too long')
        .matches(/^\d{13,19}$/, 'invalid card number format'),
    zip: yup
        .string()
        .required('zip code is required')
        .trim()
        .matches(
            /^(\d{5}(?:[-\s]\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/,
            'invalid zip code format'
        ),
    cvv: yup
        .string()
        .required('cvv is required')
        .trim()
        .matches(/^[0-9]{3,4}$/, 'invalid CVV format'),
    expiry: yup
        .string()
        .required('expiry is required')
        .trim()
        .length(5, 'invalid date'),
});

type FormData = yup.InferType<typeof schema>;

export type CreditCardDto = {
    name?: string;
    number?: string;
    expiry?: string;
    cvv?: string;
    zip?: string;
};

type UseCreditCardFormProps = {
    onCancel?: () => void;
    onProceed: (data: CreditCardDto) => void;
    isLoading?: boolean;
};

type StateNames = 'isAmExCard' | 'isNumberVisible' | 'isNumberFieldFocused';

const useCreditCardForm = ({
    onProceed,
}: UseCreditCardFormProps) => {

    const [isAmExCard, setIsAmExCard] = useState<boolean>(false);
    const [isNumberVisible, setIsNumberVisible] = useState<boolean>(false);
    const [isNumberFieldFocused, setIsNumberFieldFocused] = useState<boolean>(false);
    const numberFieldRef = useRef(null);

    const setBooleanState = (
        stateName: StateNames,
        value: boolean
    ): void => {
        switch (stateName) {
            case 'isAmExCard':
                setIsAmExCard(value);
                break;
            case 'isNumberVisible':
                setIsNumberVisible(value);
                break;
            case 'isNumberFieldFocused':
                setIsNumberFieldFocused(value);
                break;
            default:
                console.log(`useCreditCardForm > setBooleanState > ${stateName} state does not exist`);
                break;
        }
    };

    const { control, handleSubmit, setError, clearErrors, formState:{ errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const onSubmit = (data: FormData): void => {
        const parsedDate = parse(data.expiry, 'MM/yy', new Date());

        if ((isAfter(new Date(), parsedDate) || !isValid(parsedDate))) {
            setError('expiry', {
                type: 'manual',
                message: 'invalid date',
            });
        } else {
            clearErrors('expiry');
            onProceed(data);
        }
    };

    const { field: nameField } = useController({
        control,
        defaultValue: '',
        name: 'name',
    });

    const { field: numberField } = useController({
        control,
        defaultValue: '',
        name: 'number',
    });

    const { field: expiryField } = useController({
        control,
        defaultValue: '',
        name: 'expiry',
    });

    const { field: cvvField } = useController({
        control,
        defaultValue: '',
        name: 'cvv',
    });

    const { field: zipField } = useController({
        control,
        defaultValue: '',
        name: 'zip',
    });

    const onCardNumberChange = (value: string): void => {
        setIsAmExCard(['34', '37'].includes(value.slice(0, 2)));
        const numericValue = value.replace(/[^0-9]/g, '');
        numberField.onChange(numericValue);
    };

    const onExpiryChange = (value: string): void => {
        clearErrors('expiry');
        const numericValue = value.replace(/\D/g, '');

        const formattedValue = numericValue.replace(/^(.{2})/, '$1/');
        const limitedValue = formattedValue.slice(0, 5);

        expiryField.onChange(limitedValue);

        const parsedDate = parse(limitedValue, 'MM/yy', new Date());

        if (limitedValue.length === 5 && (isAfter(new Date(), parsedDate) || !isValid(parsedDate))) {
            setError('expiry', {
                type: 'manual',
                message: 'invalid date',
            });
        } else {
            clearErrors('expiry');
        }
    };

    return {
        isAmExCard,
        isNumberVisible,
        isNumberFieldFocused,
        numberFieldRef,
        nameField,
        numberField,
        cvvField,
        expiryField,
        zipField,
        onCardNumberChange,
        onExpiryChange,
        setBooleanState,
        onSubmit,
        formMethods: {
            control,
            handleSubmit,
            setError,
            clearErrors,
            errors
        }
    };
};

export default useCreditCardForm;
